import { useEffect, useState, useRef } from "react";
import "./style.css";

// Helpers
const determineDirection = (first, last) => {
  if (first < last) return "inc";
  if (first > last) return "dec";
  return "none";
};

const countUp = (val, max) => {
  const numberArray = [];
  for (let i = val; i <= max; i++) numberArray.push(i.toString());
  return numberArray;
};

const countDown = (val, max) => {
  const numberArray = [];
  for (let i = val; i >= max; i--) numberArray.push(i.toString());
  return numberArray;
};

const difference = (start, end) => {
  let longerArray;
  let shorterArray;
  let isDecreasingInLength;

  const startReversed = [...start].reverse();
  const endReversed = [...end].reverse();

  if (startReversed.length > endReversed.length) {
    longerArray = startReversed;
    shorterArray = endReversed;
    isDecreasingInLength = true;
  } else {
    longerArray = endReversed;
    shorterArray = startReversed;
  }

  const numberColumns = longerArray.reduce((acc, item, i) => {
    let arr = [];
    const comparison = shorterArray[i];

    if (item === comparison) {
      arr = [item];
    } else if (item <= comparison) {
      arr = countDown(Number(comparison), Number(item));
    } else if (item >= comparison) {
      arr = countUp(Number(comparison), Number(item));
    } else if (typeof comparison === "undefined" && !isDecreasingInLength) {
      arr = [item];
    }

    acc.push(arr);
    return acc;
  }, []);

  const numberDiff = numberColumns.reverse();

  if (isDecreasingInLength) {
    return numberDiff.map((col) => col.reverse());
  }

  return numberDiff;
};

// Assume your helpers: difference, determineDirection, countUp, countDown are imported or defined here

const AnimatedCount = ({ target, startVl = "0", shouldAnimate, duration = 400 }) => {
  const numericTarget = Number(target) || 0;
  const numericStart = Number(startVl) || 0;

  // We'll store prevValue and displayValue as strings padded to same length
  const [prevValue, setPrevValue] = useState(numericStart.toString());
  const [displayValue, setDisplayValue] = useState(numericStart.toString());
  const [isAnimating, setIsAnimating] = useState(false);

  const timeoutRef = useRef(null);
  const heightRef = useRef(null);
  const elementRef = useRef(null);

  useEffect(() => {
    const next = numericTarget.toString();
    const start = numericStart.toString();

    // If no animation or start === next, just set displayValue to next
    if (!shouldAnimate || start === next) {
      setPrevValue(next);
      setDisplayValue(next);
      setIsAnimating(false);
      return;
    }

    // Pad both strings to same length (with leading zeros)
    const maxLength = Math.max(start.length, next.length);
    const paddedStart = start.padStart(maxLength, "0");
    const paddedNext = next.padStart(maxLength, "0");

    setPrevValue(paddedStart);
    setDisplayValue(paddedNext);
    setIsAnimating(true);

    // Clear any previous timeout before setting new one
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      setIsAnimating(false);
    }, duration);

    // Cleanup on unmount or when dependencies change
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [numericTarget, numericStart, shouldAnimate, duration]);

  // Measure height once
  useEffect(() => {
    if (elementRef.current && !heightRef.current) {
      heightRef.current = elementRef.current.offsetHeight;
    }
  }, []);

  // Calculate difference animation frames
  const diff = difference([...prevValue], [...displayValue]);

  return (
    <span
      className="number-ticker"
      style={{ height: `${heightRef.current || "auto"}px` }}
    >
      {isAnimating ? (
  // map over each digit position
  [...Array(prevValue.length)].map((_, i) => {
    const oldDigit = prevValue[i];
    const newDigit = displayValue[i];
    const direction = determineDirection(oldDigit, newDigit);

    return (
      <span key={i} className="number item">
        {/* always render old and new digit together */}
        <span className={`col ${direction}`}>
          <span>{oldDigit}</span>
          <span>{newDigit}</span>
        </span>
      </span>
    );
  })
) : (
  <span ref={elementRef} className="number">
    {displayValue}
  </span>
)}
    </span>
  );
};

export default AnimatedCount;